import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
// Import Types
import type { MAU, SubscriptionLocalModule, SubscriptionPlanLocalModel } from './types';
// Import Store
import { useAppSelector } from 'store';
import { selectSubscriptionPlansData } from 'store/slices/subscriptionPlans';
import { selectPartnerSettingsSubscription } from 'store/slices/partnerSettings';
// Import Services
import { DashboardHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Components
import SubscriptionPlanWidget from './SubscriptionPlanWidget';
// Import UI Components
import { Alert, BusyLoader, Button, Icon, Modal, SkeletonLoader, Tooltip } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';

interface Props {
    handleClose: () => void;
}

const SubscriptionInfoModal: React.FC<Props> = ({ handleClose }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { Id: currentPlanId } = useAppSelector(selectPartnerSettingsSubscription) ?? {};
    const { isLoading: isLoadingSubscriptionPlans, list: subscriptionPlans } =
        useAppSelector(selectSubscriptionPlansData);

    const [mauData, setMauData] = useState<MAU | null>(null);
    const [isMAULoading, setIsMAULoading] = useState(false);

    const { getMAURequest } = useCancelableRequests({
        getMAURequest: DashboardHttpService.getMAU,
    });

    const subscriptionsPlansData = useMemo(() => {
        return subscriptionPlans.map((plan): SubscriptionPlanLocalModel => {
            const isCurrentPlan = plan.Id === currentPlanId;
            const tmpModules = cloneDeep(plan.Modules).map((module): SubscriptionLocalModule => ({ text: module }));
            if (plan.BasePlanId) {
                const foundIncludedPlan = subscriptionPlans.find((planItem) => planItem.Id === plan.BasePlanId);
                if (foundIncludedPlan) {
                    tmpModules.unshift({ isReference: true, text: foundIncludedPlan.DisplayName });
                }
            }
            return { ...plan, Modules: tmpModules, isCurrentPlan };
        });
    }, [currentPlanId, subscriptionPlans]);

    const getTodaysDate = () => {
        const today = new Date();
        return today.toISOString().slice(0, 10);
    };

    const getMAUMetrics = () => {
        const todayDate = getTodaysDate();
        setIsMAULoading(true);
        doGetRequest(getMAURequest.request, {
            queryString: {
                date: todayDate,
            },
            successCallback: (data) => {
                setMauData(data);
            },
        }).then(() => {
            setIsMAULoading(false);
        });
    };

    const init = () => {
        getMAUMetrics();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <Modal
            withPortal={true}
            closeOnClickOutside={true}
            background="dark-background"
            position="center"
            size="content-size"
            visible={true}
            closable={false}
            onCancel={handleClose}
            className="subscription-info-modal"
            title={<span>{t(l.PartnerPackagePlan)}</span>}
            footer={
                <Button appearance="minimal" onClick={handleClose}>
                    {t(l.Close)}
                </Button>
            }
        >
            <SkeletonLoader isBusy={isMAULoading}>
                <div className="mau-info">
                    <span>
                        <div className="label-text">{t(l.CurrentMAU)}</div>
                        <strong>{mauData?.currentPeriodValue ?? t(l.NA)}</strong>
                        <Tooltip text={t(l.CurrentMAUDescription)}>
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </span>
                    <span>
                        <div className="label-text">{t(l.PreviousMAU)}</div>{' '}
                        <strong>{mauData?.previousPeriodValue ?? t(l.NA)}</strong>
                        <Tooltip text={t(l.PreviousMAUDescription)}>
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </span>
                </div>
            </SkeletonLoader>

            <BusyLoader isBusy={isLoadingSubscriptionPlans}>
                <div className="plan-packages-container">
                    {subscriptionsPlansData.map((planData) => {
                        return <SubscriptionPlanWidget key={planData.Id} data={planData} />;
                    })}
                </div>
            </BusyLoader>
            <Alert
                closable={false}
                title={t(l.UpgradeSubscriptionPlanInfoMessage)}
                iconProps={{ type: 'bc-icon-info' }}
            />
        </Modal>
    );
};

export default memo(SubscriptionInfoModal);
